import styled from "@emotion/styled";
import { Box, Grid, Table, TableHead, TableCell, TableRow, Button } from "@mui/material";
import { FC, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { AnalysisResultMap, IndustryTypeMap, RadarChartColumn, SystemErrorMsg, SystemErrorTitle } from "../../constants";
import RadarChartModal from "../radarChart/RadarChartModal";
import React from "react";
import calcFinancialAnalysis from "shared/calcFinancialAnalysis";
import { sessionDisconnectedCheck } from "shared/sessionDisconnectedCheck";
import createPdf from "shared/createPdf";
import { BackdropLoading } from "shared/backdropLoading";

const HeaderLabel = styled.header`
    background-color: #41DBCE;
    color: #FFFFFF;
    height: 3rem;
    font-size: 1.5rem;
    padding-top: 0.5rem;
    padding-left: 4rem;
`;

const IndustryArea = styled(Box)(({ }) => ({
    backgroundColor: '#F2F2F2',
    textAlign: 'center',
    fontSize: '1.5rem',
    height: '4rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
}));

const TextFieldBox = styled(Box)(({ }) => ({
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#595959',
    width: '60%',
}));

const TextFieldLabel = styled(Grid)(({ }) => ({
    backgroundColor: '#595959',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const TextFieldForm = styled(Grid)(({ }) => ({
    backgroundColor: '#FFFFFF',
}));

const AnalysisResultArea = styled(Box)(({ }) => ({
    backgroundColor: '#F2F2F2',
    margin: '0 auto',
    paddingTop: '1rem',
    paddingBottom: '1rem'
}));

const ButtonArea = styled(Box)(({ }) => ({
    textAlign: 'right',
}));

const AnalysisResultButton = styled(Button)(({ }) => ({
    backgroundColor: '#01CFBE',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    height: '2.3rem',
    width: '13rem',
    borderRadius: '100rem',
    marginRight: '1.8rem',
    marginTop: '1.2rem'
}));

const AnalysisResultTableArea = styled(Box)(({ }) => ({
    backgroundColor: '#FFFFFF',
    width: '85%',
    margin: '0 auto',
}));

const BackButtonArea = styled(Box)(({ }) => ({
    backgroundColor: '#595959',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '1rem',
    height: '4rem',
    lineHeight: '4rem',
    marginTop: '1.5rem',
    marginBottom: '3rem'
}));

const screenId = 'U043'

const AnalysisResult: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [radarChartData, setRadarChartData] = useState<any>([]);
    const [fiscalYear, setFiscalYear] = useState<string>("");
    const [fiscalMonth, setFiscalMonth] = useState<string>("");
    const [otherFiscalYear1, setOtherFiscalYear1] = useState<string>("");
    const [othreFiscalMonth1, setOtherFiscalMonth1] = useState<string>("");
    const [otherFiscalYear2, setOtherFiscalYear2] = useState<string>("");
    const [othreFiscalMonth2, setOtherFiscalMonth2] = useState<string>("");
    const [showRadarChart, setShowRadarChart] = useState(false);
    const [analyzeResult, setAnalyzeResult] = useState<any>([]);
    const [createPdfRequst, setCreatePdfRequst] = useState<any>({});
    const [calculatedFlg, setCalculatedFlg] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const onClickBack = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "onClickBack", location.state.yearEdition, navigate)) {
            return
        }
        navigate('/FinancialAnalysis', { state: location.state });
    };

    React.useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", "", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const downloadPDF = async () => {
        setLoading(true)
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "downloadPDF", location.state.yearEdition, navigate)) {
            return
        }
        const result: any = await createPdf(createPdfRequst)
        // 帳票出力APIから正常終了以外が返却されたらエラー表示
        if (result != "I0000") {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
        setLoading(false)
    };

    const handleRaderChart = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "handleRaderChart", location.state.yearEdition, navigate)) {
            return
        }
        // レーダーチャート用のデータセット
        const radarChartData: { column: any; value: any; }[] = []
        let contrastRatio = '0'
        analyzeResult.forEach((v: { [key: string]: any }) => {
            contrastRatio = v.contrastRatio.replace('%', '')
            if (!Number.isFinite(Number(contrastRatio)) || Number(contrastRatio) < 0) {
                contrastRatio = '0.0'
            }
            if (v.key == "CR") {
                radarChartData.push({ column: "流動比率", value: contrastRatio })
            }
            if (v.key == "FAtER") {
                radarChartData.push({ column: "固定比率", value: contrastRatio })
            }
            if (v.key == "ITD") {
                radarChartData.push({ column: "棚卸資産回転日数", value: contrastRatio })
            }
            if (v.key == "TATD") {
                radarChartData.push({ column: "総資本回転日数", value: contrastRatio })
            }
            if (v.key == "RPM") {
                radarChartData.push({ column: "売上高経常利益率", value: contrastRatio })
            }
            if (v.key == "GPM") {
                radarChartData.push({ column: "売上高総利益率", value: contrastRatio })
            }
            if (v.key == "MSpE") {
                radarChartData.push({ column: "1人当たり月売上高", value: contrastRatio })
            }
            if (v.key == "MOPpE") {
                radarChartData.push({ column: "1人当たり月経常利益", value: contrastRatio })
            }
        });
        radarChartData.sort((a, b) => {
            return RadarChartColumn.indexOf(a.column) - RadarChartColumn.indexOf(b.column);
        });
        setRadarChartData(radarChartData)
        setShowRadarChart(true)
    };

    React.useEffect(() => {
        (async () => {
            window.scrollTo(0, 0)
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "callCalcFinancialAnalysis", location.state.yearEdition, navigate)) {
                return
            }
            let result: any = {}
            result = await callCalcFinancialAnalysis()

            let response: { [key: string]: any } = {
                res_cd: result.res_cd,
                msg_title: result.msg_title,
                msg: result.msg,
                financialRatios1: result.financialRatios1,
                financialRatios2: result.financialRatios2,
                financialRatios3: result.financialRatios3,
                standardFinancialRatios: result.standardFinancialRatios,
                contrastRatio: result.contrastRatio
            }

            // 財務分析計算APIから正常終了以外が返却されたらエラー表示
            if (response.res_cd != "I0000") {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                return
            }

            // 各決算月を0埋めする
            response.financialRatios1.fiscalMonth = response.financialRatios1.fiscalMonth.padStart(2, '0')
            response.financialRatios2.fiscalMonth = response.financialRatios2.fiscalMonth.padStart(2, '0')
            response.financialRatios3.fiscalMonth = response.financialRatios3.fiscalMonth.padStart(2, '0')

            // 比較対象の決算期に応じて表示順を並び替える
            let financialRatios: any = {}
            let otherFinancialRatios1: any = {}
            let otherFinancialRatios2: any = {}
            let otherFiscalYM1: Number = 0
            let otherFiscalYM2: Number = 0
            if (response.financialRatios1.isComparisonTarget) {
                financialRatios = response.financialRatios1
                // 残り2期の決算年月を比較
                otherFiscalYM1 = Number(response.financialRatios2.fiscalYear + response.financialRatios2.fiscalMonth) || 0
                otherFiscalYM2 = Number(response.financialRatios3.fiscalYear + response.financialRatios3.fiscalMonth) || 0
                if (otherFiscalYM1 < otherFiscalYM2) {
                    otherFinancialRatios1 = response.financialRatios2
                    otherFinancialRatios2 = response.financialRatios3
                } else {
                    otherFinancialRatios1 = response.financialRatios3
                    otherFinancialRatios2 = response.financialRatios2
                }
            } else if (response.financialRatios2.isComparisonTarget) {
                financialRatios = response.financialRatios2
                // 残り2期の決算年月を比較
                otherFiscalYM1 = Number(response.financialRatios1.fiscalYear + response.financialRatios1.fiscalMonth) || 0
                otherFiscalYM2 = Number(response.financialRatios3.fiscalYear + response.financialRatios3.fiscalMonth) || 0
                if (otherFiscalYM1 < otherFiscalYM2) {
                    otherFinancialRatios1 = response.financialRatios1
                    otherFinancialRatios2 = response.financialRatios3
                } else {
                    otherFinancialRatios1 = response.financialRatios3
                    otherFinancialRatios2 = response.financialRatios1
                }
            } else {
                financialRatios = response.financialRatios3
                // 残り2期の決算年月を比較
                otherFiscalYM1 = Number(response.financialRatios1.fiscalYear + response.financialRatios1.fiscalMonth) || 0
                otherFiscalYM2 = Number(response.financialRatios2.fiscalYear + response.financialRatios2.fiscalMonth) || 0
                if (otherFiscalYM1 < otherFiscalYM2) {
                    otherFinancialRatios1 = response.financialRatios1
                    otherFinancialRatios2 = response.financialRatios2
                } else {
                    otherFinancialRatios1 = response.financialRatios2
                    otherFinancialRatios2 = response.financialRatios1
                }
            }
            delete financialRatios.isComparisonTarget
            delete otherFinancialRatios1.isComparisonTarget
            delete otherFinancialRatios1.isComparisonTarget
            setFiscalYear(financialRatios.fiscalYear)
            setFiscalMonth(financialRatios.fiscalMonth)
            setOtherFiscalYear1(otherFinancialRatios1.fiscalYear)
            setOtherFiscalMonth1(otherFinancialRatios1.fiscalMonth)
            setOtherFiscalYear2(otherFinancialRatios2.fiscalYear)
            setOtherFiscalMonth2(otherFinancialRatios2.fiscalMonth)

            // 選択した業種に応じたレスポンスを整形する
            let resultType = ''
            if (IndustryTypeMap['製造業・一次産業'].includes(location.state.industryData.mainCategory)) {
                resultType = '製造業・一次産業'
            } else if (IndustryTypeMap['建設業'].includes(location.state.industryData.mainCategory)) {
                resultType = '建設業'
            } else {
                resultType = '販売業・運輸業・サービス業'
            }
            const analyzeResult: any = []
            AnalysisResultMap[resultType].forEach((row: { [key: string]: any }) => {
                analyzeResult.push({
                    key: row.key,
                    column: row.column,
                    type: row.type,
                    financialRatios: financialRatios[row.key],
                    otherFinancialRatios1: otherFinancialRatios1[row.key],
                    otherFinancialRatios2: otherFinancialRatios2[row.key],
                    standardFinancialRatios: response.standardFinancialRatios[row.key],
                    contrastRatio: response.contrastRatio[row.key]
                })
            })
            setAnalyzeResult(analyzeResult)
            setCalculatedFlg(true)

            const createPdfRequst: any = {
                outputTarget: "CreateFinancialAnalysisPdf",
                industryName: location.state.industryData.industryName,
                mainCategory: location.state.industryData.mainCategory,
                financialRatios1: otherFinancialRatios1,
                financialRatios2: otherFinancialRatios2,
                financialRatios3: financialRatios,
                standardFinancialRatios: response.standardFinancialRatios,
                contrastRatio: response.contrastRatio,
            }

            setCreatePdfRequst(createPdfRequst)
        })()
    }, [])

    React.useEffect(() => {
        if (calculatedFlg) {
            setLoading(false)
        }
    }, [calculatedFlg])

    // 財務分析計算APIの呼び出し
    const callCalcFinancialAnalysis = async () => {
        // BS、PLの入力値を文字列に変換する
        let fiscalYM1 = structuredClone(location.state.inputState.tableValue.fiscalYM1)
        let fiscalYM2 = structuredClone(location.state.inputState.tableValue.fiscalYM2)
        let fiscalYM3 = structuredClone(location.state.inputState.tableValue.fiscalYM3)
        for (const key in fiscalYM1["BS"]) {
            fiscalYM1["BS"][key] = String(fiscalYM1["BS"][key])
        }
        for (const key in fiscalYM2["BS"]) {
            fiscalYM2["BS"][key] = String(fiscalYM2["BS"][key])
        }
        for (const key in fiscalYM3["BS"]) {
            fiscalYM3["BS"][key] = String(fiscalYM3["BS"][key])
        }
        for (const key in fiscalYM1["PL"]) {
            fiscalYM1["PL"][key] = String(fiscalYM1["PL"][key])
        }
        for (const key in fiscalYM2["PL"]) {
            fiscalYM2["PL"][key] = String(fiscalYM2["PL"][key])
        }
        for (const key in fiscalYM3["PL"]) {
            fiscalYM3["PL"][key] = String(fiscalYM3["PL"][key])
        }
        let request = {
            mainCategory: location.state.industryData.mainCategory,
            subCategory: location.state.industryData.subCategory != "blank" ? location.state.industryData.subCategory : "",
            item: location.state.industryData.item != "blank" ? location.state.industryData.item : "",
            scaleCode: location.state.industryData.scaleCode != "blank" ? location.state.industryData.scaleCode : "",
            edition: location.state.yearEdition,
            fiscalYM1: fiscalYM1,
            fiscalYM2: fiscalYM2,
            fiscalYM3: fiscalYM3
        }
        return await calcFinancialAnalysis(request)
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <HeaderLabel>
                財務分析
            </HeaderLabel>
            <IndustryArea>
                <TextFieldBox border={1}>
                    <Grid container>
                        <TextFieldLabel size={3}>業種名</TextFieldLabel>
                        <TextFieldForm size={9}>
                            {location.state.industryData.industryName}
                        </TextFieldForm>
                    </Grid>
                </TextFieldBox>
            </IndustryArea>
            {calculatedFlg
                ?
                <AnalysisResultArea>
                    <AnalysisResultTableArea>
                        <ButtonArea>
                            <AnalysisResultButton startIcon={<img src={`${process.env.PUBLIC_URL}/Icons/analyzeIconWhite.svg`} width="20px" />} variant="contained" color="secondary" onClick={() => handleRaderChart()}>チャートを表示</AnalysisResultButton>
                            <AnalysisResultButton startIcon={<img src={`${process.env.PUBLIC_URL}/Icons/downloadIcon.svg`} width="15px" />} variant="contained" color="secondary" onClick={() => downloadPDF()}>PDFダウンロード</AnalysisResultButton>
                        </ButtonArea>
                        <Box p={3}>
                            <Table sx={{ border: '1px solid' }}>
                                <TableHead>
                                    <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid black', height: '2rem', fontSize: '0.8rem', paddingLeft: '0', paddingRight: '0', paddingTop: '0.2rem', paddingBottom: '0.2rem', textAlign: 'center' } }}>
                                        <TableCell style={{ width: '30%', backgroundColor: '#B5F9E8' }}>項目</TableCell>
                                        <TableCell style={{ width: '14%', backgroundColor: '#B5F9E8' }}>{otherFiscalYear1 != "" ? otherFiscalYear1 + "年" + othreFiscalMonth1 + "月期" : ""}</TableCell>
                                        <TableCell style={{ width: '14%', backgroundColor: '#B5F9E8' }}>{otherFiscalYear2 != "" ? otherFiscalYear2 + "年" + othreFiscalMonth2 + "月期" : ""}</TableCell>
                                        <TableCell style={{ width: '14%', backgroundColor: '#00A99C', color: '#FFFFFF' }}>{fiscalYear}年{fiscalMonth}月期</TableCell>
                                        <TableCell style={{ width: '10%', backgroundColor: '#B5F9E8' }}>業種平均</TableCell>
                                        <TableCell style={{ width: '7%', backgroundColor: '#B5F9E8' }}>判断基準</TableCell>
                                        <TableCell style={{ width: '11%', backgroundColor: '#B5F9E8' }}>対比率（%）</TableCell>
                                    </TableRow>
                                </TableHead>
                                {analyzeResult.map((row: any) => (
                                    <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid black', height: '0.3rem', fontSize: '0.8rem', paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0', paddingBottom: '0', } }}>
                                        <TableCell sx={{ textAlign: 'left' }}>{row.column}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{otherFiscalYear1 != "" ? row.otherFinancialRatios1 : ""}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{otherFiscalYear2 != "" ? row.otherFinancialRatios2 : ""}</TableCell>
                                        <TableCell sx={{ textAlign: 'right', '&:nth-of-type(even)': { backgroundColor: '#FFF3D4' } }}>{row.financialRatios}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{row.standardFinancialRatios}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.type === 1 ? <img src={`${process.env.PUBLIC_URL}/Icons/arrowUp.svg`} width="15px" style={{ position: "relative", top: '1px' }} /> : row.type === 2 ? <img src={`${process.env.PUBLIC_URL}/Icons/arrowDown.svg`} width="15px" style={{ position: "relative", top: '5px' }} /> : 'ー'}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{row.contrastRatio}</TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        </Box>
                    </AnalysisResultTableArea>
                </AnalysisResultArea>
                :
                <></>
            }

            <BackButtonArea>
                <div>
                    <span onClick={onClickBack} style={{ cursor: 'pointer' }}>
                        <img src={`${process.env.PUBLIC_URL}/Icons/returnIcon.svg`} width="13px" />&nbsp;&nbsp;戻る
                    </span>
                </div>
            </BackButtonArea>
            <RadarChartModal isOpen={showRadarChart} data={radarChartData} fiscalYear={fiscalYear} handleCloseChart={() => setShowRadarChart(false)} />
        </>
    );
}

export default AnalysisResult;
